import React from 'react';
import { X, Package, AlertCircle } from 'lucide-react';

const ItemDetailModal = ({ isOpen, item, onClose }) => {
  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-lg w-full sm:max-w-md max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between rounded-t-3xl">
          <h3 className="text-lg font-bold text-gray-900">Product Details</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Product Image */}
          {item.productImage && (
            <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              <img 
                src={item.productImage} 
                alt={item.productName} 
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Product Name */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{item.productName}</h2>
          </div>

          {/* Bottle Size/Variant - Prominent Display */}
          {item.variant && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">Bottle Size / Volume</p>
              <p className="text-2xl font-bold text-blue-600">{item.variant}</p>
            </div>
          )}

          {/* Quantity */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 font-medium">Quantity</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{item.quantity}</p>
          </div>

          {/* Price Information */}
          <div className="grid grid-cols-2 gap-4">
            {item.unitPrice && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 font-medium uppercase">Unit Price</p>
                <p className="text-xl font-bold text-gray-900 mt-1">₹{parseFloat(item.unitPrice).toFixed(2)}</p>
              </div>
            )}
            {item.subtotal && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-600 font-medium uppercase">Subtotal</p>
                <p className="text-xl font-bold text-blue-600 mt-1">₹{parseFloat(item.subtotal).toFixed(2)}</p>
              </div>
            )}
          </div>

          {/* SKU and other details */}
          <div className="space-y-3 border-t border-gray-200 pt-4">
            {item.sku && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">SKU</span>
                <span className="font-mono text-sm font-semibold text-gray-900">{item.sku}</span>
              </div>
            )}
            {item.productDescription && (
              <div className="flex flex-col gap-2">
                <span className="text-sm text-gray-600">Description</span>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{item.productDescription}</p>
              </div>
            )}
            {item.productCategory && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Category</span>
                <span className="text-sm font-medium text-gray-900">{item.productCategory}</span>
              </div>
            )}
          </div>

          {/* Delivery Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">Delivery Info</p>
              <p className="text-xs text-yellow-700 mt-1">
                {item.variant ? `Deliver ${item.quantity} × ${item.variant} to customer` : `Deliver ${item.quantity} units to customer`}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full mt-4 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;
